import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './LoginPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const LoginPage: React.FC = () => {
  const [passcode, setPasscode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStatus, setForgotStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [forgotError, setForgotError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if there's a pre-filled PIN from new user registration
    if (location.state?.preFilledPin) {
      setPasscode(location.state.preFilledPin);
    }
  }, [location.state]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 5) {
      setPasscode(value);
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passcode.length !== 5) {
      setError('Please enter a 5-digit user code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/validate-passcode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: passcode }),
      });

      const data = await response.json();

      if (data.valid) {
        const canAddDependents = data.can_add_dependents === true && data.is_dependent !== true;
        localStorage.setItem('currentPasscode', passcode);
        localStorage.setItem('canAddDependents', canAddDependents ? 'true' : 'false');
        navigate('/success', { state: { passcode, canAddDependents } });
      } else {
        setError(data.message || 'Invalid user code');
      }
    } catch (err) {
      setError('Failed to connect to server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenForgotModal = () => {
    setShowForgotModal(true);
    setForgotEmail('');
    setForgotStatus('idle');
    setForgotError('');
  };

  const handleCloseForgotModal = () => {
    setShowForgotModal(false);
  };

  const handleForgotSubmit = async () => {
    if (!forgotEmail.trim()) {
      setForgotError('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotEmail.trim())) {
      setForgotError('Please enter a valid email address');
      return;
    }

    setForgotStatus('sending');
    setForgotError('');

    try {
      const response = await fetch(`${API_BASE_URL}/forgot-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setForgotStatus('success');
        setTimeout(() => {
          setShowForgotModal(false);
          setForgotStatus('idle');
        }, 3000);
      } else {
        setForgotStatus('error');
        setForgotError(data.message || 'Failed to send email');
      }
    } catch (err) {
      setForgotStatus('error');
      setForgotError('Failed to connect to server');
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="logo-section">
          <div className="logo">
            <img src="/VolunteerHeart.png" alt="FabMo Volunteer" className="logo-image" />
          </div>
          <p className="tagline">Welcome! Login with your User Code</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form" autoComplete="off">
          <div className="input-group">
            <label htmlFor="passcode" className="input-label">
              Enter 5-Digit User Code
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              id="passcode"
              name="passcode"
              value={passcode}
              onChange={handleInputChange}
              placeholder="00000"
              className={`passcode-input ${error ? 'error' : ''}`}
              maxLength={5}
              disabled={isLoading}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-form-type="other"
            />
            {error && <p className="error-message">{error}</p>}
          </div>

          <button
            type="submit"
            className={`submit-button ${isLoading ? 'loading' : ''}`}
            disabled={isLoading || passcode.length !== 5}
          >
            {isLoading ? (
              <div className="loading-spinner"></div>
            ) : (
              'Submit'
            )}
          </button>
        </form>

        <div className="new-user-section">
          <div className="button-row">
            <button
              onClick={handleOpenForgotModal}
              className="forgot-code-button"
            >
              Forgot Code
            </button>
            <button
              onClick={() => navigate('/admin-login')}
              className="admin-button"
            >
              Admin
            </button>
          </div>
          <button
            onClick={() => navigate('/new-user')}
            className="new-user-button-full"
          >
            New User Registration
          </button>
        </div>
      </div>

      {/* Forgot Code Modal */}
      {showForgotModal && (
        <div className="modal-overlay" onClick={handleCloseForgotModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Forgot Your Code?</h2>
              <button className="modal-close" onClick={handleCloseForgotModal}>×</button>
            </div>
            
            {forgotStatus === 'success' ? (
              <div className="modal-success">
                <div className="success-checkmark">✓</div>
                <p>If an account exists with that email, we've sent your login information!</p>
              </div>
            ) : (
              <>
                <div className="modal-body">
                  <p className="modal-description">
                    Enter the email address associated with your account and we'll send you your User Code.
                  </p>
                  <div className="form-group">
                    <label htmlFor="forgot-email">Email Address <span className="required-star">*</span></label>
                    <input
                      type="email"
                      id="forgot-email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="form-input"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  {forgotError && (
                    <div className="modal-error">{forgotError}</div>
                  )}
                </div>

                <div className="modal-footer">
                  <button 
                    className="modal-cancel-button" 
                    onClick={handleCloseForgotModal}
                    disabled={forgotStatus === 'sending'}
                  >
                    Cancel
                  </button>
                  <button 
                    className="modal-send-button" 
                    onClick={handleForgotSubmit}
                    disabled={forgotStatus === 'sending'}
                  >
                    {forgotStatus === 'sending' ? 'Sending...' : 'Send Code'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
